import React, {
    useEffect,
    useMemo,
    useState,
} from 'react'

import PulseLoader from 'react-spinners/PulseLoader'

import {
    useGetEvalCategoriesQuery,
    useCreateNewEvalCategoryMutation,
} from './evalCategorysApiSlice'

import {
    useGetEvalDimensionsQuery,
    useCreateNewEvalDimensionMutation,
} from './evalDimensionsApiSlice'

import {
    useGetCoachUserSessionEvalMatrixsQuery,
    useCreateNewCoachUserSessionEvalMatrixMutation,
    useUpdateCoachUserSessionEvalMatrixMutation,
} from './coachUserSessionEvalMatrixsApiSlice'

// 28 Oct 2026 - Coach Controlled Evaluation Sharing
import EtCoachUserSessionEvalSharing
    from './EtCoachUserSessionEvalSharing'


/*
|--------------------------------------------------------------------------
| 01 - Reference ID Helper
|--------------------------------------------------------------------------
*/

const getId = value => {

    if (!value) return ''

    if (typeof value === 'string') {
        return value
    }

    return String(value._id || value.id || '')

}

/*
|--------------------------------------------------------------------------
| 27 Oct 2026 - Evaluation Date/Time Helpers
|--------------------------------------------------------------------------
|
| datetime-local uses the coach's local date and time.
|
| MongoDB stores an absolute UTC timestamp.
|
|--------------------------------------------------------------------------
*/

const toLocalDateTimeInput = value => {

    const date = value
        ? new Date(value)
        : new Date()

    if (Number.isNaN(date.getTime())) {
        return ''
    }

    const pad = number =>
        String(number).padStart(2, '0')

    return (
        `${date.getFullYear()}-` +
        `${pad(date.getMonth() + 1)}-` +
        `${pad(date.getDate())}T` +
        `${pad(date.getHours())}:` +
        `${pad(date.getMinutes())}`
    )

}


const toUTCEvaluationDate = localValue => {

    if (!localValue) {
        return null
    }

    const date = new Date(localValue)

    if (Number.isNaN(date.getTime())) {
        return null
    }

    return date.toISOString()

}


/*
|--------------------------------------------------------------------------
| 02 - Normalized RTK Query Collection
|--------------------------------------------------------------------------
*/

const getEntities = data => {

    return (data?.ids || [])
        .map(id => data.entities[id])
        .filter(Boolean)

}


/*
|--------------------------------------------------------------------------
| 03 - Coach User Session Evaluation Panel
|--------------------------------------------------------------------------
|
| One reflection note -> one evaluation matrix.
|
| Does not create a matrix until an evaluation is saved.
|
| Cohort remains an attribute and is not used as
| part of evaluation identity.
|
|--------------------------------------------------------------------------
*/

const EtCoachUserSessionEvalPanel = ({

    coachId,
    userId,

    orgId,
    trailId,
    sessionId,

    coachOrgTrailSessionPadId,

    reflectionNoteId,
    reflectionNoteType,

}) => {

    // First Created - 22 Oct 2026
    // Author - Atul Pandey
    // Updated - 27 Oct 2026 - Evaluated At Changes
    // Updated - 28 Oct 2026 - Coach Controlled Evaluation Sharing


    /*
    |--------------------------------------------------------------------------
    | 04 - Evaluation Master Data
    |--------------------------------------------------------------------------
    |
    | Include inactive definitions so historical
    | evaluation dimensions can still be displayed.
    |
    */

    const {
        data: categoryData,
        isLoading: isCategoriesLoading,
        isError: isCategoriesError,
    } = useGetEvalCategoriesQuery({
        active: 'all',
    })

    const {
        data: dimensionData,
        isLoading: isDimensionsLoading,
        isError: isDimensionsError,
    } = useGetEvalDimensionsQuery({
        active: 'all',
    })


    /*
    |--------------------------------------------------------------------------
    | 05 - Existing Coach Evaluation Matrices
    |--------------------------------------------------------------------------
    */

    const {
        data: matrixData,
        isLoading: isMatricesLoading,
        isError: isMatricesError,
    } = useGetCoachUserSessionEvalMatrixsQuery(
        {
            coachId,
            userId,
            orgId,
            trailId,
            sessionId,
        },
        {
            skip:
                !coachId ||
                !userId ||
                !orgId ||
                !trailId ||
                !sessionId ||
                !reflectionNoteId,
        }
    )


    /*
    |--------------------------------------------------------------------------
    | 06 - Mutations
    |--------------------------------------------------------------------------
    */

    const [
        createNewEvalCategory,
        {
            isLoading: isCreatingCategory,
        },
    ] = useCreateNewEvalCategoryMutation()

    const [
        createNewEvalDimension,
        {
            isLoading: isCreatingDimension,
        },
    ] = useCreateNewEvalDimensionMutation()

    const [
        createNewCoachUserSessionEvalMatrix,
        {
            isLoading: isCreatingMatrix,
        },
    ] = useCreateNewCoachUserSessionEvalMatrixMutation()

    const [
        updateCoachUserSessionEvalMatrix,
        {
            isLoading: isUpdatingMatrix,
        },
    ] = useUpdateCoachUserSessionEvalMatrixMutation()


    /*
    |--------------------------------------------------------------------------
    | 07 - Resolve Existing Evaluation Matrix
    |--------------------------------------------------------------------------
    */

    const existingMatrix = useMemo(() => {

        return getEntities(matrixData).find(
            matrix =>
                getId(matrix.coachOrgTrailSessionPadId) ===
                    getId(coachOrgTrailSessionPadId) &&

                getId(matrix.reflectionNoteId) ===
                    getId(reflectionNoteId) &&

                matrix.reflectionNoteType ===
                    reflectionNoteType
        ) || null

    }, [
        matrixData,
        coachOrgTrailSessionPadId,
        reflectionNoteId,
        reflectionNoteType,
    ])


    /*
    |--------------------------------------------------------------------------
    | 08 - Category And Dimension Collections
    |--------------------------------------------------------------------------
    */

    const categories = useMemo(
        () => getEntities(categoryData),
        [categoryData]
    )

    const dimensions = useMemo(
        () => getEntities(dimensionData),
        [dimensionData]
    )

    const existingDimensionIds = useMemo(
        () => new Set(
            (existingMatrix?.sessionEvals || [])
                .map(item => getId(item.dimensionId))
        ),
        [existingMatrix]
    )


    /*
    |--------------------------------------------------------------------------
    | 09 - Local Evaluation Draft
    |--------------------------------------------------------------------------
    |
    | Key:
    |   dimensionId
    |
    | Value:
    |   score: string (blank or 1-5)
    |   evalNote: string
    |
    */

    const [draftEvals, setDraftEvals] = useState({})

    /*
    |--------------------------------------------------------------------------
    | 27 Oct 2026 - Actual Evaluation Date/Time
    |--------------------------------------------------------------------------
    */

    const [evaluationDate, setEvaluationDate] = useState(
        () => toLocalDateTimeInput(new Date())
    )

    const [initialEvaluationDate, setInitialEvaluationDate] =
        useState('')

    useEffect(() => {

        const nextDraft = {}

        ;(existingMatrix?.sessionEvals || []).forEach(
            item => {

                const dimensionId = getId(
                    item.dimensionId
                )

                if (!dimensionId) return

                nextDraft[dimensionId] = {
                    score:
                        item.score === null ||
                        item.score === undefined
                            ? ''
                            : String(item.score),

                    evalNote: item.evalNote || '',
                }

            }
        )

        setDraftEvals(nextDraft)

        /*
        |--------------------------------------------------------------------------
        | 27 Oct 2026 - Populate Existing Evaluation Date
        |--------------------------------------------------------------------------
        |
        | Older evaluations may not yet have evaluatedAt.
        | In that case, show createdAt as a provisional date.
        |
        */

        const persistedEvaluationDate =
            existingMatrix?.evaluatedAt ||
            existingMatrix?.createdAt

        const localEvaluationDate = toLocalDateTimeInput(
            persistedEvaluationDate || new Date()
        )

        setEvaluationDate(localEvaluationDate)

        setInitialEvaluationDate(localEvaluationDate)

    }, [
        reflectionNoteId,
        existingMatrix?._id,
        existingMatrix?.updatedAt,
    ])


    /*
    |--------------------------------------------------------------------------
    | 10 - Inline Master Definition Creation
    |--------------------------------------------------------------------------
    */

    const [showAddCategory, setShowAddCategory] =
        useState(false)

    const [newCategoryName, setNewCategoryName] =
        useState('')

    const [newCategoryDesc, setNewCategoryDesc] =
        useState('')

    const [addDimensionCategoryId, setAddDimensionCategoryId] =
        useState('')

    const [newDimensionName, setNewDimensionName] =
        useState('')

    const [newDimensionDesc, setNewDimensionDesc] =
        useState('')

    const [showAddDimension, setShowAddDimension] =
        useState(false)

    const [isEditing, setIsEditing] = useState(false)

    const [statusMessage, setStatusMessage] = useState('')

    const [errorMessage, setErrorMessage] = useState('')


    /*
    |--------------------------------------------------------------------------
    | 11 - Evaluation Draft Handler
    |--------------------------------------------------------------------------
    */

    const updateDraft = (
        dimensionId,
        field,
        value
    ) => {

        setDraftEvals(previous => ({

            ...previous,

            [dimensionId]: {
                score:
                    previous[dimensionId]?.score ?? '',

                evalNote:
                    previous[dimensionId]?.evalNote ?? '',

                [field]: value,
            },

        }))

        setStatusMessage('')
        setErrorMessage('')

    }


    /*
    |--------------------------------------------------------------------------
    | 12 - Create Category Inline
    |--------------------------------------------------------------------------
    */

    const handleCreateCategory = async () => {

        if (!newCategoryName.trim()) {
            setErrorMessage('Enter a category name.')
            return
        }

        try {

            setErrorMessage('')

            const createdCategory =
                await createNewEvalCategory({

                    categoryName:
                        newCategoryName.trim(),

                    categoryDesc:
                        newCategoryDesc.trim(),

                }).unwrap()

            setNewCategoryName('')
            setNewCategoryDesc('')
            setShowAddCategory(false)

            setAddDimensionCategoryId(
                getId(createdCategory)
            )

            setStatusMessage(
                'Evaluation category created.'
            )

        } catch (error) {

            setErrorMessage(
                error?.data?.message ||
                'Unable to create evaluation category.'
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 13 - Create Dimension Inline
    |--------------------------------------------------------------------------
    */

    const handleCreateDimension = async () => {

        if (
            !addDimensionCategoryId ||
            !newDimensionName.trim()
        ) {
            setErrorMessage(
                'Select a category and enter a dimension name.'
            )
            return
        }

        try {

            setErrorMessage('')

            await createNewEvalDimension({

                categoryId:
                    addDimensionCategoryId,

                dimensionName:
                    newDimensionName.trim(),

                dimensionDesc:
                    newDimensionDesc.trim(),

            }).unwrap()

            setNewDimensionName('')
            setNewDimensionDesc('')
            setShowAddDimension(false)

            setStatusMessage(
                'Evaluation dimension created.'
            )

        } catch (error) {

            setErrorMessage(
                error?.data?.message ||
                'Unable to create evaluation dimension.'
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 14 - Build Evaluation Save Changes
    |--------------------------------------------------------------------------
    |
    | Preserve unaffected dimensions.
    |
    | Score and note timestamps remain managed by
    | the backend evaluation controller.
    |
    | Empty score + empty note:
    |   Existing dimension -> remove from matrix
    |   New dimension      -> no operation
    |
    */

    const buildEvaluationChanges = () => {

        const existingByDimensionId = new Map(

            (existingMatrix?.sessionEvals || [])
                .map(item => [
                    getId(item.dimensionId),
                    item,
                ])
        )

        const sessionEvals = []
        const removeDimensionIds = []

        for (const [dimensionId, draft] of Object.entries(
            draftEvals
        )) {

            const score =
                draft.score === ''
                    ? null
                    : Number(draft.score)

            const evalNote =
                draft.evalNote || ''

            const existing =
                existingByDimensionId.get(dimensionId)

            if (
                score === null &&
                !evalNote.trim()
            ) {

                if (existing) {
                    removeDimensionIds.push(
                        dimensionId
                    )
                }

                continue
            }

            const previousScore =
                existing?.score ?? null

            const previousNote =
                existing?.evalNote || ''

            if (
                !existing ||
                previousScore !== score ||
                previousNote !== evalNote
            ) {

                sessionEvals.push({
                    dimensionId,
                    score,
                    evalNote,
                })

            }

        }

        return {
            sessionEvals,
            removeDimensionIds,
        }

    }


    /*
    |--------------------------------------------------------------------------
    | 15 - Save Evaluation Matrix
    |--------------------------------------------------------------------------
    */

    const handleSaveEvaluation = async () => {

        if (
            !coachOrgTrailSessionPadId ||
            !reflectionNoteId
        ) {
            setErrorMessage(
                'Save the reflection note before evaluating it.'
            )
            return
        }

        const {
            sessionEvals,
            removeDimensionIds,
        } = buildEvaluationChanges()

        /*
        |--------------------------------------------------------------------------
        | 27 Oct 2026 - Resolve Actual Evaluation Timestamp
        |--------------------------------------------------------------------------
        */

        const evaluatedAt = toUTCEvaluationDate(
            evaluationDate
        )

        if (!evaluatedAt) {
            setErrorMessage(
                'Select a valid evaluation date and time.'
            )
            return
        }

        const evaluationDateChanged =
            evaluationDate !== initialEvaluationDate

        if (
            sessionEvals.length === 0 &&
            removeDimensionIds.length === 0 &&
            !evaluationDateChanged                  // 27 Oct 2026
        ) {
            setStatusMessage(
                'No evaluation changes to save.'
            )
            return
        }

        const evaluatedAtToSave =
            existingMatrix &&
            !evaluationDateChanged
                ? (
                    existingMatrix.evaluatedAt ||
                    existingMatrix.createdAt
                )
                : evaluatedAt

        try {

            setErrorMessage('')
            setStatusMessage('')

            if (existingMatrix?._id) {

                await updateCoachUserSessionEvalMatrix({

                    id: existingMatrix._id,

                    evaluatedAt: evaluatedAtToSave,    // 27 Oct 2026

                    sessionEvals,
                    removeDimensionIds,

                }).unwrap()

            } else {

                await createNewCoachUserSessionEvalMatrix({

                    coachOrgTrailSessionPadId,

                    reflectionNoteId,
                    reflectionNoteType,

                    evaluatedAt: evaluatedAtToSave,    // 27 Oct 2026

                    sessionEvals,

                }).unwrap()

            }

            setInitialEvaluationDate(evaluationDate) // 27 Oct 2026

            setIsEditing(false)

            setStatusMessage(
                'Session evaluation saved successfully.'
            )

        } catch (error) {

            setErrorMessage(
                error?.data?.message ||
                'Unable to save session evaluation.'
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 16 - Active / Historical Category Display
    |--------------------------------------------------------------------------
    |
    | Active master definitions appear for scoring.
    |
    | Inactive definitions remain visible if referenced
    | by this reflection note's existing evaluation.
    |
    */

    const visibleCategories = useMemo(() => {

        const referencedCategoryIds = new Set(

            dimensions
                .filter(
                    dimension =>
                        existingDimensionIds.has(
                            getId(dimension)
                        )
                )
                .map(
                    dimension =>
                        getId(dimension.categoryId)
                )
        )

        return categories
            .filter(
                category =>
                    category.active !== false ||
                    referencedCategoryIds.has(
                        getId(category)
                    )
            )
            .slice()
            .sort(
                (a, b) =>
                    (a.displayOrder ?? 0) -
                    (b.displayOrder ?? 0) ||
                    (a.categoryName || '').localeCompare(
                        b.categoryName || ''
                    )
            )

    }, [
        categories,
        dimensions,
        existingDimensionIds,
    ])


    /*
    |--------------------------------------------------------------------------
    | 17 - Loading And Error States
    |--------------------------------------------------------------------------
    */

    if (
        isCategoriesLoading ||
        isDimensionsLoading ||
        isMatricesLoading
    ) {

        return (
            <div className="flex justify-center p-3">
                <PulseLoader
                    color="#ea580c"
                    size={6}
                />
            </div>
        )

    }

    if (
        isCategoriesError ||
        isDimensionsError ||
        isMatricesError
    ) {

        return (
            <p className="m-2 text-xs text-red-600">
                Unable to load session evaluation data.
            </p>
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 18 - Evaluation UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="mt-3 rounded border border-orange-200 bg-white p-3 font-poppins">

            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-orange-100 pb-2">

                <div>

                    <p className="text-xs font-bold text-orange-600">
                        Session Progress Evaluation
                    </p>

                    <p className="mt-1 text-[10px] text-gray-500">
                        Evaluate capability and readiness dimensions
                        against this reflection note.
                    </p>

                </div>

                <div className="flex items-center gap-2">

                    {existingMatrix && (
                        <span className="rounded-full bg-lime-50 px-2 py-1 text-[9px] font-semibold text-lime-700">
                            Evaluation recorded
                        </span>
                    )}

                    <button
                        type="button"
                        onClick={() =>
                            setIsEditing(previous => !previous)
                        }
                        className="rounded-full border border-orange-300 px-3 py-1 text-[10px] font-bold text-orange-600 hover:bg-orange-50"
                    >
                        {isEditing ? 'Close Editor' : 'Evaluate'}
                    </button>

                </div>

            </div>


            {isEditing && (

                <div className="mt-3">

                    {/* 27 Oct 2026 - Actual Evaluation Date/Time */}

                    <div className="mb-3 rounded border border-orange-200 bg-orange-50 p-3">

                        <div className="flex flex-wrap items-center justify-between gap-3">

                            <div>

                                <label
                                    htmlFor={`evaluatedAt-${reflectionNoteId}`}
                                    className="block text-xs font-bold text-gray-700"
                                >
                                    Evaluation Date &amp; Time
                                </label>

                                <p className="mt-1 text-[10px] text-gray-500">
                                    When the evaluation actually took place,
                                    independent of the reflection note date.
                                </p>

                            </div>

                            <input
                                id={`evaluatedAt-${reflectionNoteId}`}
                                type="datetime-local"
                                value={evaluationDate}
                                onChange={event => {
                                    setEvaluationDate(event.target.value)
                                    setErrorMessage('')
                                    setStatusMessage('')
                                }}
                                className="rounded border border-orange-200 bg-white px-3 py-2 text-xs text-gray-700"
                            />

                        </div>

                        {existingMatrix &&
                            !existingMatrix.evaluatedAt && (

                            <p className="mt-2 text-[10px] italic text-amber-700">
                                Historical evaluation: currently using the
                                recorded date. Please verify the actual
                                evaluation date before saving.
                            </p>

                        )}

                    </div>

                    {visibleCategories.map(category => {

                        const categoryId = getId(category)

                        const categoryDimensions = dimensions
                            .filter(dimension => {

                                const belongsToCategory =
                                    getId(dimension.categoryId) ===
                                    categoryId

                                const visible =
                                    dimension.active !== false &&
                                    category.active !== false

                                return (
                                    belongsToCategory &&
                                    (
                                        visible ||
                                        existingDimensionIds.has(
                                            getId(dimension)
                                        )
                                    )
                                )

                            })
                            .sort(
                                (a, b) =>
                                    (a.displayOrder ?? 0) -
                                    (b.displayOrder ?? 0) ||
                                    (a.dimensionName || '').localeCompare(
                                        b.dimensionName || ''
                                    )
                            )

                        return (

                            <div
                                key={categoryId}
                                className="mb-3 rounded bg-gray-50 p-2"
                            >

                                <div className="flex items-center justify-between gap-2">

                                    <div>

                                        <p className="text-xs font-bold text-gray-700">
                                            {category.categoryName}
                                        </p>

                                        {category.categoryDesc && (
                                            <p className="mt-1 text-[10px] text-gray-500">
                                                {category.categoryDesc}
                                            </p>
                                        )}

                                    </div>

                                    {category.active !== false && (

                                        <button
                                            type="button"
                                            onClick={() => {

                                                setAddDimensionCategoryId(
                                                    categoryId
                                                )

                                                setShowAddDimension(true)

                                            }}
                                            className="rounded-full border border-orange-200 px-2 py-1 text-[10px] font-semibold text-orange-600"
                                        >
                                            + Dimension
                                        </button>

                                    )}

                                </div>


                                {categoryDimensions.map(dimension => {

                                    const dimensionId =
                                        getId(dimension)

                                    const draft =
                                        draftEvals[dimensionId] || {
                                            score: '',
                                            evalNote: '',
                                        }

                                    const historical =
                                        dimension.active === false ||
                                        category.active === false

                                    return (

                                        <div
                                            key={dimensionId}
                                            className="mt-2 rounded border border-gray-200 bg-white p-2"
                                        >

                                            <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-start">

                                                <div className="md:col-span-4">

                                                    <p className="text-[11px] font-semibold text-gray-700">
                                                        {dimension.dimensionName}
                                                    </p>

                                                    {dimension.dimensionDesc && (
                                                        <p className="mt-1 text-[10px] text-gray-500">
                                                            {dimension.dimensionDesc}
                                                        </p>
                                                    )}

                                                    {historical && (
                                                        <p className="mt-1 text-[9px] italic text-gray-400">
                                                            Inactive historical dimension
                                                        </p>
                                                    )}

                                                </div>

                                                <div className="md:col-span-2">

                                                    <label className="mb-1 block text-[10px] text-gray-500">
                                                        Score
                                                    </label>

                                                    <select
                                                        value={draft.score}
                                                        onChange={event =>
                                                            updateDraft(
                                                                dimensionId,
                                                                'score',
                                                                event.target.value
                                                            )
                                                        }
                                                        className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                                                    >
                                                        <option value="">
                                                            Not scored
                                                        </option>

                                                        {[1, 2, 3, 4, 5].map(
                                                            score => (
                                                                <option
                                                                    key={score}
                                                                    value={String(score)}
                                                                >
                                                                    {score}
                                                                </option>
                                                            )
                                                        )}
                                                    </select>

                                                </div>

                                                <div className="md:col-span-6">

                                                    <label className="mb-1 block text-[10px] text-gray-500">
                                                        Evaluation Notes
                                                    </label>

                                                    <textarea
                                                        rows={2}
                                                        value={draft.evalNote}
                                                        onChange={event =>
                                                            updateDraft(
                                                                dimensionId,
                                                                'evalNote',
                                                                event.target.value
                                                            )
                                                        }
                                                        placeholder="Coach's observations..."
                                                        className="w-full rounded border border-gray-200 px-2 py-1 text-xs text-gray-700"
                                                    />

                                                </div>

                                            </div>

                                        </div>

                                    )

                                })}

                                {categoryDimensions.length === 0 && (
                                    <p className="mt-2 text-[10px] italic text-gray-400">
                                        No active dimensions in this category.
                                    </p>
                                )}

                            </div>

                        )

                    })}


                    <div className="mb-3 flex flex-wrap gap-2">

                        <button
                            type="button"
                            onClick={() =>
                                setShowAddCategory(previous => !previous)
                            }
                            className="rounded-full border border-orange-300 bg-white px-3 py-1 text-[10px] font-semibold text-orange-600"
                        >
                            + New Category
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                setShowAddDimension(previous => !previous)
                            }
                            className="rounded-full border border-orange-300 bg-white px-3 py-1 text-[10px] font-semibold text-orange-600"
                        >
                            + New Dimension
                        </button>

                    </div>


                    {showAddCategory && (

                        <div className="mb-3 rounded bg-orange-50 p-3">

                            <p className="mb-2 text-xs font-bold text-gray-700">
                                Create Evaluation Category
                            </p>

                            <input
                                value={newCategoryName}
                                onChange={event =>
                                    setNewCategoryName(event.target.value)
                                }
                                placeholder="Category name"
                                className="mb-2 w-full rounded border border-gray-200 p-2 text-xs"
                            />

                            <textarea
                                value={newCategoryDesc}
                                onChange={event =>
                                    setNewCategoryDesc(event.target.value)
                                }
                                placeholder="Category description (optional)"
                                rows={2}
                                className="mb-2 w-full rounded border border-gray-200 p-2 text-xs"
                            />

                            <button
                                type="button"
                                disabled={isCreatingCategory}
                                onClick={handleCreateCategory}
                                className="rounded-full bg-orange-600 px-4 py-1 text-xs font-semibold text-white disabled:opacity-50"
                            >
                                {isCreatingCategory
                                    ? 'Creating...'
                                    : 'Create Category'}
                            </button>

                        </div>

                    )}


                    {showAddDimension && (

                        <div className="mb-3 rounded bg-orange-50 p-3">

                            <p className="mb-2 text-xs font-bold text-gray-700">
                                Create Evaluation Dimension
                            </p>

                            <select
                                value={addDimensionCategoryId}
                                onChange={event =>
                                    setAddDimensionCategoryId(
                                        event.target.value
                                    )
                                }
                                className="mb-2 w-full rounded border border-gray-200 p-2 text-xs"
                            >
                                <option value="">
                                    Select category
                                </option>

                                {categories
                                    .filter(
                                        category =>
                                            category.active !== false
                                    )
                                    .map(category => (
                                        <option
                                            key={getId(category)}
                                            value={getId(category)}
                                        >
                                            {category.categoryName}
                                        </option>
                                    ))}
                            </select>

                            <input
                                value={newDimensionName}
                                onChange={event =>
                                    setNewDimensionName(event.target.value)
                                }
                                placeholder="Dimension name"
                                className="mb-2 w-full rounded border border-gray-200 p-2 text-xs"
                            />

                            <textarea
                                value={newDimensionDesc}
                                onChange={event =>
                                    setNewDimensionDesc(event.target.value)
                                }
                                placeholder="Dimension description (optional)"
                                rows={2}
                                className="mb-2 w-full rounded border border-gray-200 p-2 text-xs"
                            />

                            <button
                                type="button"
                                disabled={isCreatingDimension}
                                onClick={handleCreateDimension}
                                className="rounded-full bg-orange-600 px-4 py-1 text-xs font-semibold text-white disabled:opacity-50"
                            >
                                {isCreatingDimension
                                    ? 'Creating...'
                                    : 'Create Dimension'}
                            </button>

                        </div>

                    )}


                    <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-3">

                        <button
                            type="button"
                            disabled={
                                isCreatingMatrix ||
                                isUpdatingMatrix
                            }
                            onClick={handleSaveEvaluation}
                            className="rounded-full bg-lime-600 px-4 py-2 text-xs font-bold text-white hover:bg-lime-700 disabled:opacity-50"
                        >
                            {isCreatingMatrix || isUpdatingMatrix
                                ? 'Saving...'
                                : 'Save Evaluation'}
                        </button>

                        <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="rounded-full bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                        >
                            Close
                        </button>

                    </div>

                </div>

            )}


            {statusMessage && (
                <p className="mt-2 text-[10px] font-semibold text-lime-700">
                    {statusMessage}
                </p>
            )}

            {errorMessage && (
                <p className="mt-2 text-[10px] font-semibold text-red-600">
                    {errorMessage}
                </p>
            )}

            {!existingMatrix && !isEditing && (
                <p className="mt-2 text-[10px] italic text-gray-400">
                    No evaluation recorded for this reflection note.
                </p>
            )}

            {existingMatrix && !isEditing && (
                <p className="mt-2 text-[10px] text-gray-500">
                    {
                        existingMatrix.sessionEvals?.filter(
                            item =>
                                item.score !== null ||
                                Boolean(item.evalNote?.trim())
                        ).length || 0
                    } evaluated dimensions
                </p>
            )}

            {/*----------------------------------------------------------
                28 Oct 2026 - Step 11D
                Coach Controlled Evaluation Sharing
                ----------------------------------------------------------*/}

                {existingMatrix && !isEditing && (

                    <EtCoachUserSessionEvalSharing

                        key={existingMatrix._id}

                        evaluationMatrix={existingMatrix}

                        categories={categories}

                        dimensions={dimensions}

                    />

            )}

        </div>

    )

}


export default EtCoachUserSessionEvalPanel